/**
 * GET `app_config` — typed keys, doc defaults, and org fallbacks when the request fails.
 *
 * Resolution order:
 * - **app_config responded** (even `{}`): API values for keys present, else doc defaults.
 * - **app_config failed / null**: org/initial fallbacks where v3 already had logic, else doc defaults.
 *
 * Keys not wired to UI yet are still parsed and exposed via {@link getMergedAppConfig} for future use.
 */

import { applyPwaUiSetOverrides } from './pwaUiSetOverrides';

export interface AppConfigOrgFallbackInput {
  show_actual_recipes?: number | boolean | string;
  show_online_orders?: string | boolean | number;
}

export type GuestFlowType = 'type1' | 'type2' | 'type3';
export type GuestMenuListMode = 'full' | 'custom' | 'random';
export type MenuPageStyle = 'type1' | 'type2' | 'type3' | 'type4';
export type SubscriptionListStructure = 'category' | 'tag' | 'merged' | 'premium' | 'all';
export type SubscriptionListStyle = 'list' | 'grid';
export type PortionTextMode = 'portion' | 'person';
export type ShowHideFlag = 'show' | 'hide';
export type AddressDeliveryAssignmentMode = 'use_1' | 'use_2' | 'disabled';
export type DashboardVariant = 'set1' | 'set2' | 'set3';
export type MainScreenVariant = 'classic' | 'banner_guest' | 'hero_dock';
export type AllergyPickerMode = 'split' | 'combined';

export type NumberDigitsMode = 'locale' | 'western';

export interface AppConfig {
  show_consultations: boolean;
  show_online_orders: boolean;
  show_actual_recipes: boolean;
  show_community: boolean;
  /** When true: journey wizard entry on main screen + dashboard shortcuts. */
  is_journey: boolean;
  /**
   * When true: journey calories come from GET/POST calculate API (current flow).
   * When false: results are computed in-app (local logic; stub until implemented).
   */
  is_journey_by_api: boolean;
  guest_flow_type: GuestFlowType;
  guest_menu_list: GuestMenuListMode;
  /**
   * Logged-out Main → menu browse: total days in the guest date window (min 7).
   * Layout: 3 past, today, remaining future. Values > 30 = unbounded future browsing.
   */
  guest_menu_size: number;
  menu_page_style: MenuPageStyle;
  subscription_list_structure: SubscriptionListStructure;
  subscription_list_style: SubscriptionListStyle;
  portion_text: PortionTextMode;
  /**
   * Subscription cards: when true, show explicit portion/persons when present on the plan/sub.
   * Accepts `true`/`false` or `show`/`hide` from API; stored as boolean.
   * When shown, scaled macros + calorie range are forced off for that object.
   */
  show_portion: boolean;
  /**
   * Master nutrition UI gate. When false: hide macro bars, item macro chips,
   * calendar/date-selection macros, and related nutrition chrome.
   * Replaces the former composite of `scaled_text` + `calories_limit`.
   */
  is_nutrition_ui_visible: boolean;
  /** Parsed only — not used for gating. Kept for future fine-grained controls. */
  scaled_text: ShowHideFlag;
  /**
   * Subscription cards: when true, show min–max calories (unless portion overrides).
   * Accepts `true`/`false` or `show`/`hide` from API; stored as boolean.
   */
  calories_limit: boolean;
  calories_limit_colors: string;
  has_last_name: boolean;
  /**
   * Subscription cards: when true, show scaled protein + carbs (unless portion overrides).
   * Accepts `true`/`false` or `show`/`hide` from API; stored as boolean.
   */
  show_subscription_scaled_macros: boolean;
  /**
   * Subscription list: when true, show Regular/Custom tab bar (when API returns custom plans).
   * Accepts `true`/`false` or `show`/`hide` from API; stored as boolean.
   */
  show_custom_plans: boolean;
  address_delivery_assignment_mode: AddressDeliveryAssignmentMode;
  /** When true: batch save + navigation guards (v3). When false: auto-save per selection (v1). */
  save_menu_flow: boolean;
  /** Dashboard stat-card layout: set1 (v1 pill macros), set2 (v2 card shell), set3 (v3 ring macros). */
  dashboard_variant: DashboardVariant;
  /** Calendar screen chrome: set1 (studio), set2 (menu strip + v2 card), set3 (studio v3). */
  calendar_variant: DashboardVariant;
  /** Classic menu flow chrome within `MenuFlowScreen`: set1 / set2 / set3. Not daily-menu tab routing. */
  menu_flow_variant: DashboardVariant;
  /** Main auth landing: classic tiles (v1), banner guest CTAs (v2), or hero dock (v3). */
  main_screen_variant: MainScreenVariant;
  /** Allergies picker: split APIs (v1) or combined allergy_dislikes API (v3). */
  allergy_picker_mode: AllergyPickerMode;
  /**
   * Logged-out Main → menu browse: when true, show Ramadan/futoor/suhoor food types as usual.
   * When false, hide food types whose labels contain ramadan / ramazan / futoor / suhoor.
   * Logged-in menus and plan selling are never filtered by this flag.
   */
  logged_out_ramadan_menu: boolean;
  /**
   * Classic main screen: when true, show synced client `logo.svg` (`ClientLogoSvg`).
   * Never shows a non-SVG fallback. When false, logo is hidden even if SVG exists / no banner.
   */
  show_logo_classic: boolean;
  /**
   * Classic main screen banner video/YouTube: when true, loop indefinitely.
   * When false, play once per screen focus (restarts on re-nav / remount).
   */
  banner_loop: boolean;
  /** Accounts tab hub layout: set1 (v1 cards + list rows), set2 (v2 banner header + grid), set3 (v3 studio hub). */
  accounts_variant: DashboardVariant;
  /**
   * Display numerals for UI counts/dates via `formatNumber`.
   * - `locale`: Arabic-Indic when app locale is `ar` (default).
   * - `western`: always Latin 0–9 (future toggle; API payloads stay ASCII regardless).
   */
  number_digits: NumberDigitsMode;
}

/** Doc defaults — used when app_config omits a key or responds with empty data. */
export const DEFAULT_APP_CONFIG: AppConfig = {
  show_consultations: true,
  show_online_orders: true,
  show_actual_recipes: false,
  show_community: false,
  is_journey: true,
  is_journey_by_api: true,
  guest_flow_type: 'type1',
  guest_menu_list: 'full',
  guest_menu_size: 7,
  menu_page_style: 'type1',
  subscription_list_structure: 'category',
  subscription_list_style: 'list',
  portion_text: 'portion',
  show_portion: false,
  is_nutrition_ui_visible: true,
  scaled_text: 'show',
  calories_limit: true,
  calories_limit_colors: 'any',
  has_last_name: true,
  show_subscription_scaled_macros: false,
  show_custom_plans: false,
  address_delivery_assignment_mode: 'disabled',
  save_menu_flow: false,
  dashboard_variant: 'set1',
  calendar_variant: 'set1',
  menu_flow_variant: 'set1',
  main_screen_variant: 'classic',
  allergy_picker_mode: 'split',
  logged_out_ramadan_menu: false,
  show_logo_classic: false,
  banner_loop: true,
  accounts_variant: 'set1',
  number_digits: 'locale',
};

const GUEST_FLOW_TYPES: GuestFlowType[] = ['type1', 'type2', 'type3'];
const DASHBOARD_VARIANTS: DashboardVariant[] = ['set1', 'set2', 'set3'];
const GUEST_MENU_LIST_MODES: GuestMenuListMode[] = ['full', 'custom', 'random'];
const MENU_PAGE_STYLES: MenuPageStyle[] = ['type1', 'type2', 'type3', 'type4'];
const SUBSCRIPTION_LIST_STRUCTURES: SubscriptionListStructure[] = ['category', 'tag', 'merged', 'premium', 'all'];
const SUBSCRIPTION_LIST_STYLES: SubscriptionListStyle[] = ['list', 'grid'];
const PORTION_TEXT_MODES: PortionTextMode[] = ['portion', 'person'];
const SHOW_HIDE_FLAGS: ShowHideFlag[] = ['show', 'hide'];
const ADDRESS_DELIVERY_ASSIGNMENT_MODES: AddressDeliveryAssignmentMode[] = ['use_1', 'use_2', 'disabled'];
const MAIN_SCREEN_VARIANTS: MainScreenVariant[] = ['classic', 'banner_guest', 'hero_dock'];
const ALLERGY_PICKER_MODES: AllergyPickerMode[] = ['split', 'combined'];
const NUMBER_DIGITS_MODES: NumberDigitsMode[] = ['locale', 'western'];

let cached_app_config: AppConfig = { ...DEFAULT_APP_CONFIG };
let app_config_payload_received = false;

export function isAppConfigPayloadReceived(): boolean {
  return app_config_payload_received;
}

export function getMergedAppConfig(): Readonly<AppConfig> {
  return cached_app_config;
}

export function isCommunityFeatureEnabled(): boolean {
  return cached_app_config.show_community;
}

export function isJourneyEnabled(): boolean {
  return cached_app_config.is_journey;
}

/** When true, journey calories use the remote calculate API; when false, in-app logic. */
export function isJourneyByApiEnabled(): boolean {
  return cached_app_config.is_journey_by_api;
}

export function isConsultationsFeatureEnabled(): boolean {
  return cached_app_config.show_consultations;
}

export function isOnlineOrdersFeatureEnabled(): boolean {
  return cached_app_config.show_online_orders;
}

export function isShowActualRecipesEnabledFromAppConfig(): boolean {
  return cached_app_config.show_actual_recipes;
}

export function isHasLastNameEnabled(): boolean {
  return cached_app_config.has_last_name;
}

export function isShowSubscriptionScaledMacrosEnabled(): boolean {
  return cached_app_config.show_subscription_scaled_macros;
}

export function isShowCustomPlansEnabled(): boolean {
  return cached_app_config.show_custom_plans;
}

export function getMenuPageStyle(): MenuPageStyle {
  return cached_app_config.menu_page_style;
}

export function getSubscriptionListStructure(): SubscriptionListStructure {
  return cached_app_config.subscription_list_structure;
}

export function getSubscriptionListStyle(): SubscriptionListStyle {
  return cached_app_config.subscription_list_style;
}

export function getGuestFlowType(): GuestFlowType {
  return cached_app_config.guest_flow_type;
}

/** Maps `menu_page_style` → menu tab flow.
 * type1 → classic menu (`src/menu`), type2 → daily menu (`src/dailyMenu`),
 * type3 → food menu (`src/foodMenu`), type4 → food daily menu (`src/foodDailyMenu`).
 * type1+2 share `domains/menu`; type3+4 share `domains/foodMenu`.
 */
export function resolveMenuTabFlowFromMenuPageStyle(
  style: MenuPageStyle = cached_app_config.menu_page_style,
): 'classic_menu' | 'daily_menu' | 'food_menu' | 'food_daily_menu' {
  if (style === 'type2') {
    return 'daily_menu';
  }
  if (style === 'type3') {
    return 'food_menu';
  }
  if (style === 'type4') {
    return 'food_daily_menu';
  }
  return 'classic_menu';
}

/** True when the menu tab uses the foodMenu data/domain stack (type3 / type4). */
export function isFoodMenuTabFlow(
  style: MenuPageStyle = cached_app_config.menu_page_style,
): boolean {
  const flow = resolveMenuTabFlowFromMenuPageStyle(style);
  return flow === 'food_menu' || flow === 'food_daily_menu';
}

export function getAddressDeliveryAssignmentMode(): AddressDeliveryAssignmentMode {
  return cached_app_config.address_delivery_assignment_mode;
}

export function isSaveMenuFlowEnabled(): boolean {
  return cached_app_config.save_menu_flow;
}

export function getDashboardVariant(): DashboardVariant {
  return cached_app_config.dashboard_variant;
}

export function getCalendarVariant(): DashboardVariant {
  return cached_app_config.calendar_variant;
}

export function getMenuFlowVariant(): DashboardVariant {
  return cached_app_config.menu_flow_variant;
}

export function getAccountsVariant(): DashboardVariant {
  return cached_app_config.accounts_variant;
}

export function getMainScreenVariant(): MainScreenVariant {
  return cached_app_config.main_screen_variant;
}

export function getAllergyPickerMode(): AllergyPickerMode {
  return cached_app_config.allergy_picker_mode;
}

/** When true, logged-out Main menu browse shows Ramadan-related food types. */
export function isLoggedOutRamadanMenuEnabled(): boolean {
  return cached_app_config.logged_out_ramadan_menu;
}

/** Classic main: show client `logo.svg` only (no fallback). Default / fallback: false. */
export function isShowLogoClassicEnabled(): boolean {
  return cached_app_config.show_logo_classic;
}

/** Classic main banner video loop. Default / fallback: true. */
export function isBannerLoopEnabled(): boolean {
  return cached_app_config.banner_loop;
}

export function getGuestMenuListMode(): GuestMenuListMode {
  return cached_app_config.guest_menu_list;
}

/** Logged-out guest menu browse window size (`guest_menu_size`). Default / fallback: 7. */
export function getGuestMenuSize(): number {
  return cached_app_config.guest_menu_size;
}

export function getPortionTextMode(): PortionTextMode {
  return cached_app_config.portion_text;
}

/** Subscription card portion/persons gate (`show_portion`). Default / fallback: false. */
export function isShowPortionEnabled(): boolean {
  return cached_app_config.show_portion;
}

/** Master gate for nutrition UI (`is_nutrition_ui_visible`). Default / fallback: true. */
export function isNutritionUiVisible(): boolean {
  return cached_app_config.is_nutrition_ui_visible;
}

/** @deprecated Parsed only — does not gate UI. Prefer `isNutritionUiVisible`. */
export function isScaledTextVisible(): boolean {
  return cached_app_config.scaled_text === 'show';
}

/** Subscription card calorie min–max gate (`calories_limit`). */
export function isCaloriesLimitVisible(): boolean {
  return cached_app_config.calories_limit;
}

export function getCaloriesLimitColorsMode(): string {
  return cached_app_config.calories_limit_colors;
}

/** When `western`, UI `formatNumber` always uses Latin digits even if locale is `ar`. */
export function getNumberDigitsMode(): NumberDigitsMode {
  return cached_app_config.number_digits;
}

export function usesWesternDigits(): boolean {
  return cached_app_config.number_digits === 'western';
}

export function resetAppConfigCache(): void {
  cached_app_config = { ...DEFAULT_APP_CONFIG };
  app_config_payload_received = false;
}

function readConfigRecord(payload: unknown): Record<string, unknown> | null {
  if (payload == null || typeof payload !== 'object') {
    return null;
  }
  const root = payload as Record<string, unknown>;

  /** Backend shape: `{ data: { app_config: { show_community: "true", ... } } }`. */
  const data = root.data;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const data_record = data as Record<string, unknown>;
    const nested = data_record.app_config;
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
    return data_record;
  }

  const root_nested = root.app_config;
  if (root_nested != null && typeof root_nested === 'object' && !Array.isArray(root_nested)) {
    return root_nested as Record<string, unknown>;
  }

  return root;
}

function readField(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = record[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }
  return undefined;
}

export function parseBooleanConfigValue(value: unknown): boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0) {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on') {
      return true;
    }
    if (normalized === '0' || normalized === 'false' || normalized === 'no' || normalized === 'off') {
      return false;
    }
  }
  return undefined;
}

/**
 * Accepts boolean-like values (`true`/`false`/`1`/`0`/…) and show/hide tokens.
 * Used for gates that may arrive as either shape from `app_config`.
 */
export function parseBooleanOrShowHideConfigValue(value: unknown): boolean | undefined {
  const as_bool = parseBooleanConfigValue(value);
  if (as_bool !== undefined) {
    return as_bool;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase().replace(/_/g, '');
    if (normalized === 'show') {
      return true;
    }
    if (normalized === 'hide') {
      return false;
    }
  }
  return undefined;
}

function normalizeEnumToken(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, '');
}

function parseEnumValue<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  if (value == null) {
    return undefined;
  }
  const normalized = normalizeEnumToken(String(value));
  if (!normalized) {
    return undefined;
  }
  return allowed.find(option => normalizeEnumToken(option) === normalized);
}

function parseOptionalString(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : undefined;
}

function parsePositiveIntConfigValue(value: unknown): number | undefined {
  if (value == null || String(value).trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  const floored = Math.floor(parsed);
  return floored >= 0 ? floored : undefined;
}

function parsePartialAppConfigFromPayload(payload: unknown): Partial<AppConfig> {
  const record = readConfigRecord(payload);
  if (record == null) {
    return {};
  }

  const partial: Partial<AppConfig> = {};

  const show_consultations = parseBooleanConfigValue(
    readField(record, ['show_consultations', 'show_consultation']),
  );
  if (show_consultations !== undefined) {
    partial.show_consultations = show_consultations;
  }

  const show_online_orders = parseBooleanConfigValue(readField(record, ['show_online_orders']));
  if (show_online_orders !== undefined) {
    partial.show_online_orders = show_online_orders;
  }

  const show_actual_recipes = parseBooleanConfigValue(readField(record, ['show_actual_recipes']));
  if (show_actual_recipes !== undefined) {
    partial.show_actual_recipes = show_actual_recipes;
  }

  const show_community = parseBooleanConfigValue(readField(record, ['show_community']));
  if (show_community !== undefined) {
    partial.show_community = show_community;
  }

  const is_journey = parseBooleanConfigValue(readField(record, ['is_journey']));
  if (is_journey !== undefined) {
    partial.is_journey = is_journey;
  }

  const is_journey_by_api = parseBooleanConfigValue(readField(record, ['is_journey_by_api']));
  if (is_journey_by_api !== undefined) {
    partial.is_journey_by_api = is_journey_by_api;
  }

  const guest_flow_type = parseEnumValue(readField(record, ['guest_flow_type']), GUEST_FLOW_TYPES);
  if (guest_flow_type) {
    partial.guest_flow_type = guest_flow_type;
  }

  const guest_menu_list = parseEnumValue(readField(record, ['guest_menu_list']), GUEST_MENU_LIST_MODES);
  if (guest_menu_list) {
    partial.guest_menu_list = guest_menu_list;
  }

  const guest_menu_size = parsePositiveIntConfigValue(readField(record, ['guest_menu_size']));
  if (guest_menu_size !== undefined) {
    partial.guest_menu_size = guest_menu_size;
  }

  const menu_page_style = parseEnumValue(readField(record, ['menu_page_style']), MENU_PAGE_STYLES);
  if (menu_page_style) {
    partial.menu_page_style = menu_page_style;
  }

  const subscription_list_structure = parseEnumValue(
    readField(record, ['subscription_list_structure']),
    SUBSCRIPTION_LIST_STRUCTURES,
  );
  if (subscription_list_structure) {
    partial.subscription_list_structure = subscription_list_structure;
  }

  const subscription_list_style = parseEnumValue(
    readField(record, ['subscription_list_style']),
    SUBSCRIPTION_LIST_STYLES,
  );
  if (subscription_list_style) {
    partial.subscription_list_style = subscription_list_style;
  }

  const portion_text = parseEnumValue(readField(record, ['portion_text']), PORTION_TEXT_MODES);
  if (portion_text) {
    partial.portion_text = portion_text;
  }

  const show_portion = parseBooleanOrShowHideConfigValue(readField(record, ['show_portion']));
  if (show_portion !== undefined) {
    partial.show_portion = show_portion;
  }

  const is_nutrition_ui_visible = parseBooleanConfigValue(readField(record, ['is_nutrition_ui_visible']));
  if (is_nutrition_ui_visible !== undefined) {
    partial.is_nutrition_ui_visible = is_nutrition_ui_visible;
  }

  const scaled_text = parseEnumValue(readField(record, ['scaled_text']), SHOW_HIDE_FLAGS);
  if (scaled_text) {
    partial.scaled_text = scaled_text;
  }

  const calories_limit = parseBooleanOrShowHideConfigValue(readField(record, ['calories_limit']));
  if (calories_limit !== undefined) {
    partial.calories_limit = calories_limit;
  }

  const calories_limit_colors = parseOptionalString(readField(record, ['calories_limit_colors']));
  if (calories_limit_colors) {
    partial.calories_limit_colors = calories_limit_colors;
  }

  const has_last_name = parseBooleanConfigValue(readField(record, ['has_last_name']));
  if (has_last_name !== undefined) {
    partial.has_last_name = has_last_name;
  }

  const show_subscription_scaled_macros = parseBooleanOrShowHideConfigValue(
    readField(record, ['show_subscription_scaled_macros']),
  );
  if (show_subscription_scaled_macros !== undefined) {
    partial.show_subscription_scaled_macros = show_subscription_scaled_macros;
  }

  const show_custom_plans = parseBooleanOrShowHideConfigValue(readField(record, ['show_custom_plans']));
  if (show_custom_plans !== undefined) {
    partial.show_custom_plans = show_custom_plans;
  }

  const address_delivery_assignment_mode = parseEnumValue(
    readField(record, ['address_delivery_assignment_mode']),
    ADDRESS_DELIVERY_ASSIGNMENT_MODES,
  );
  if (address_delivery_assignment_mode) {
    partial.address_delivery_assignment_mode = address_delivery_assignment_mode;
  }

  const save_menu_flow = parseBooleanConfigValue(readField(record, ['save_menu_flow']));
  if (save_menu_flow !== undefined) {
    partial.save_menu_flow = save_menu_flow;
  }

  const dashboard_variant = parseEnumValue(readField(record, ['dashboard_variant']), DASHBOARD_VARIANTS);
  if (dashboard_variant) {
    partial.dashboard_variant = dashboard_variant;
  }

  const calendar_variant = parseEnumValue(readField(record, ['calendar_variant']), DASHBOARD_VARIANTS);
  if (calendar_variant) {
    partial.calendar_variant = calendar_variant;
  }

  const menu_flow_variant = parseEnumValue(readField(record, ['menu_flow_variant']), DASHBOARD_VARIANTS);
  if (menu_flow_variant) {
    partial.menu_flow_variant = menu_flow_variant;
  }

  const main_screen_variant = parseEnumValue(readField(record, ['main_screen_variant']), MAIN_SCREEN_VARIANTS);
  if (main_screen_variant) {
    partial.main_screen_variant = main_screen_variant;
  }

  const allergy_picker_mode = parseEnumValue(readField(record, ['allergy_picker_mode']), ALLERGY_PICKER_MODES);
  if (allergy_picker_mode) {
    partial.allergy_picker_mode = allergy_picker_mode;
  }

  const logged_out_ramadan_menu = parseBooleanConfigValue(readField(record, ['logged_out_ramadan_menu']));
  if (logged_out_ramadan_menu !== undefined) {
    partial.logged_out_ramadan_menu = logged_out_ramadan_menu;
  }

  const show_logo_classic = parseBooleanConfigValue(readField(record, ['show_logo_classic']));
  if (show_logo_classic !== undefined) {
    partial.show_logo_classic = show_logo_classic;
  }

  const banner_loop = parseBooleanConfigValue(readField(record, ['banner_loop']));
  if (banner_loop !== undefined) {
    partial.banner_loop = banner_loop;
  }

  const accounts_variant = parseEnumValue(readField(record, ['accounts_variant']), DASHBOARD_VARIANTS);
  if (accounts_variant) {
    partial.accounts_variant = accounts_variant;
  }

  const number_digits = parseEnumValue(
    readField(record, ['number_digits', 'numeric_digits']),
    NUMBER_DIGITS_MODES,
  );
  if (number_digits) {
    partial.number_digits = number_digits;
  } else {
    const western_flag = parseBooleanConfigValue(readField(record, ['use_western_digits']));
    if (western_flag === true) {
      partial.number_digits = 'western';
    } else if (western_flag === false) {
      partial.number_digits = 'locale';
    }
  }

  return partial;
}

function resolveActualRecipesFromOrg(organization: AppConfigOrgFallbackInput | null | undefined): boolean | undefined {
  return parseBooleanConfigValue(organization?.show_actual_recipes);
}

function resolveOnlineOrdersFromOrg(organization: AppConfigOrgFallbackInput | null | undefined): boolean | undefined {
  return parseBooleanConfigValue(organization?.show_online_orders);
}

function resolveOrgFallbackConfig(organization: AppConfigOrgFallbackInput | null | undefined): Partial<AppConfig> {
  const fallback: Partial<AppConfig> = {};
  const actual_recipes = resolveActualRecipesFromOrg(organization);
  if (actual_recipes !== undefined) {
    fallback.show_actual_recipes = actual_recipes;
  }
  const online_orders = resolveOnlineOrdersFromOrg(organization);
  if (online_orders !== undefined) {
    fallback.show_online_orders = online_orders;
  }
  return fallback;
}

/**
 * Apply GET `app_config` result after `initial` is cached.
 * @param payload - axios `data`, or `null` when the request failed.
 */
export function applyAppConfigPayload(
  payload: unknown | null,
  organization: AppConfigOrgFallbackInput | null | undefined,
): void {
  app_config_payload_received = payload != null;

  if (payload != null) {
    const parsed = parsePartialAppConfigFromPayload(payload);
    cached_app_config = applyPwaUiSetOverrides({
      ...DEFAULT_APP_CONFIG,
      ...parsed,
    });
    return;
  }

  cached_app_config = applyPwaUiSetOverrides({
    ...DEFAULT_APP_CONFIG,
    ...resolveOrgFallbackConfig(organization),
  });
}
